import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { useAppDispatch, useAppSelector } from 'store'
import $business from 'store/business'
import $customer from 'store/customer'
import $service, { ServiceType } from 'store/service'
import $resource from 'store/resource'
import $wlist from 'store/wlist'

import App from 'components/App'

import styles from './AppointmentWlistSuccess.module.scss'

export type Props = {
  onClose: () => void
}

const AppointmentWlistSuccess: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const settings = useAppSelector($business.get.settings)
  const services = useAppSelector($service.get.selected)
  const resource = useAppSelector($resource.get.selected)
  const formatFrom = useAppSelector($wlist.get.formatTime('from'))
  const formatTo = useAppSelector($wlist.get.formatTime('to'))
  const temp = useAppSelector(({ $customer }) => $customer.temp)
  const customer = useAppSelector(({ $customer }) => $customer.data)
  const bookingFieldValues = useAppSelector(({ $customer }) => $customer.bookingFieldValues)
  const savedRef = useRef(false)
  const refreshedRef = useRef(false)

  useEffect(() => {
    if (!savedRef.current && temp.phone !== '' && temp.name !== '') {
      savedRef.current = true
      const existingCustomerTemp = localStorage.getItem('customerTemp')
      const existingData = existingCustomerTemp ? JSON.parse(existingCustomerTemp) : {}
      
      localStorage.setItem('customerTemp', JSON.stringify({
        ...existingData,
        ...temp,
        bookingFieldValues: { ...existingData.bookingFieldValues, ...bookingFieldValues }
      }))
      dispatch($customer.set.loggedIn(true))
      dispatch($customer.set.bookingFieldValues({}))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temp.phone, temp.name, bookingFieldValues])

  useEffect(() => {
    const refreshCustomerData = async () => {
      if (!refreshedRef.current) {
        refreshedRef.current = true
        
        // Delay slightly to ensure backend has processed the update
        setTimeout(async () => {
          if (customer?.id && customer?.phone) {
            // For registered customers, fetch updated customer data
            const result = await $customer.api.get({ number: customer.phone })
            if (result && result.status === 'success') {
              const { allow_book, customer: updatedCustomer, status, customers, unfilled_fields } = result
              dispatch($customer.set.data({ allow_book, customer: updatedCustomer, status, customers }))
              dispatch($customer.set.unfilled_fields(unfilled_fields))
            }
          } else if (temp?.phone) {
            // For temporary customers, fetch unfilled fields
            const result = await $customer.api.getUnfilledFields()
            if (result && result.status === 'success') {
              dispatch($customer.set.unfilled_fields(result.unfilled_fields))
            }
          }
        }, 500)
      }
    }

    refreshCustomerData()
  }, [customer?.id, customer?.phone, temp?.phone, dispatch])

  return  (
    <App.Flex column aCenter gap={6} className={styles.container}>
      <App.Flex center className={styles.icon}>
        <App.Icon icon="check" xl primary />
      </App.Flex>

      <App.Text center large bold>{t('Successfully added to waiting list')}</App.Text>

      <App.Text center muted>{t('Your requested date and time have been successfully added to the waiting list. We will notify you as soon as a slot becomes available. Thank you for your patience.')}</App.Text>

      <App.Flex column fullWidth gap={2} className={styles.details}>
        <App.Text bold>{services.map((service: ServiceType) => service.name).join(', ')}</App.Text>

        <App.Flex column gap={1}>
          <App.Text xs gray>{t('Details')}:</App.Text>

          <App.Flex row wrap gap={2}>
            <App.Tag icon="clock">{moment(formatFrom).format('D')} {t(moment(formatFrom).format('MMMM'))}, {moment(formatFrom).format('HH:mm')} - {moment(formatTo).format('HH:mm')}</App.Tag>
            {resource?.id ? <App.Tag icon="user">{resource.name}</App.Tag> : null}
            {settings.display_price ? <App.Tag>₪{services.reduce((acc: number, service: ServiceType) => acc + Number(service.price), 0)}</App.Tag> : null}
          </App.Flex>
        </App.Flex>
      </App.Flex>

      <App.Button fullWidth onClick={onClose}>{t('Got it!')}</App.Button>
    </App.Flex>
  )
}

export default AppointmentWlistSuccess